/**
 * Stripe embedded payment widget. Mirrors FortisPaymentWidget's
 * contract: mounts an inline card form and emits the payment token
 * (Stripe PaymentMethod id) via onTokenReady when the customer clicks
 * "Save Card". Initialized with a SetupIntent client_secret so card
 * save + 3DS auth happen inline; the server charges the PaymentIntent.
 */
import React, {useMemo, useState} from 'react';
import {loadStripe, type Stripe} from '@stripe/stripe-js';
import {Elements, PaymentElement, useElements, useStripe} from '@stripe/react-stripe-js';

interface StripePaymentWidgetProps {
    clientSecret: string;
    publishableKey: string;
    onTokenReady: (paymentMethodId: string) => void;
    onError: (message: string) => void;
    disabled?: boolean;
}

const stripeCache: Record<string, Promise<Stripe | null>> = {};
function getStripePromise(publishableKey: string): Promise<Stripe | null> {
    if (!stripeCache[publishableKey]) stripeCache[publishableKey] = loadStripe(publishableKey);
    return stripeCache[publishableKey];
}

const InnerForm: React.FC<Omit<StripePaymentWidgetProps, 'publishableKey' | 'clientSecret'>> = ({
    onTokenReady,
    onError,
    disabled,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [saved, setSaved] = useState(false);

    async function handleSave() {
        if (!stripe || !elements) return;
        setSubmitting(true);
        const {error, setupIntent} = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
        });
        setSubmitting(false);
        if (error) {
            onError(error.message ?? 'Card could not be saved. Please check the details and try again.');
            return;
        }
        const pm = typeof setupIntent?.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent?.payment_method?.id;
        if (!pm) {
            onError('Card setup did not complete. Please try again.');
            return;
        }
        setSaved(true);
        onTokenReady(pm);
    }

    return (
        <div>
            <PaymentElement options={{layout: 'tabs'}} />
            <button
                type="button"
                onClick={handleSave}
                disabled={disabled || submitting || saved || !stripe}
                className="mt-4 w-full rounded-lg bg-mld-primary px-4 py-2 text-white disabled:opacity-60"
            >
                {saved ? 'Card Saved' : submitting ? 'Saving…' : 'Save Card'}
            </button>
        </div>
    );
};

const StripePaymentWidget: React.FC<StripePaymentWidgetProps> = ({clientSecret, publishableKey, ...rest}) => {
    const stripePromise = useMemo(() => getStripePromise(publishableKey), [publishableKey]);
    return (
        <Elements stripe={stripePromise} options={{clientSecret, appearance: {theme: 'stripe'}}}>
            <InnerForm {...rest} />
        </Elements>
    );
};

export default StripePaymentWidget;
